import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from 'styled-components';
import { ActionMenu, colors2, globalInputElemHover, globalInputElemPadding, UI_BODY_SEMIBOLD_SM, icons, Divider, TextInput, SwitchSingle } from "oolib";
import { ButtonGhost, ButtonPrimary, ButtonSecondary, ButtonTertiary } from "oolib/dist/v2/components/Buttons";
import { globalInputElemFocused_v2 } from "oolib/dist/v2/themes/globalStyles";
import { Z_INDEX } from "../../styled";
import { StyledReportsHeaderContainer, StyledReportsHeaderActions, StyledReportsHeaderButtons, StyledEditableTitleHover, StyledReportsHeaderTitleSection } from "./styled";
import { BreadcrumbHeader } from "@/components/UI/BreadcrumbHeader";
import { InlineEditableInput } from "@/components/inputs/InlineEditableInput";
import { CollabLockedField } from "@/components/collab/CollabLockedField";
import { PopoverMenu } from "../PopoverMenu";
import { AlertButton } from "../ReportSettingForm";
import {
  useReportMeta,
  useReportModals,
  useReportData,
  useReportMode,
  useReportSnapshot,
  useReportCollab
} from "../../ReportLandingContext";
import { useShareReport } from "../../utils/useShareReport";
import { useSetReportFeatured } from "../../queries";
import { ShareReportModal } from "../ShareReportModal";

const ReportOptionsMenu = ({
  showOptionsDropdown,
  setShowOptionsDropdown,
  localReportMeta,
  setLocalReportMeta,
  setShowDeleteModal,
  reportData,
  canEdit,
}) => {
  const { collabActive, reportCollab } = useReportCollab();
  // Featured saves on toggle through its own endpoint, so it lands whether
  // or not the report is published — and in collab, where nothing is.
  const { mutate: setFeatured, isLoading: settingFeatured } = useSetReportFeatured();
  const [featured, setFeaturedLocal] = useState(!!reportData?.featured);
  useEffect(() => {
    setFeaturedLocal(!!reportData?.featured);
  }, [reportData?.featured]);
  return (
    <PopoverMenu
      isOpen={showOptionsDropdown}
      onClose={() => setShowOptionsDropdown(false)}
      zIndex={Z_INDEX.REPORTS_HEADER + 1}
      trigger={
        <ButtonGhost
          onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
          icon="GearSix"
          S
        >
          Options
        </ButtonGhost>
      }
    >
        <div style={{ padding: "1.4rem", display: "flex", flexDirection: "column", gap: "1.4rem"}}>
          {/* subtitle's Y.Doc key doubles as its lock valuePath (same convention
              as PPV3's main.title); with no active session there is no lock to
              hold and the field renders bare */}
          <CollabLockedField
            session={collabActive ? reportCollab : null}
            valuePath="subtitle"
          >
            <TextInput
              label={"Report Description"}
              value={localReportMeta.subtitle}
              onChange={(id, v) => setLocalReportMeta({ subtitle: v })}
            />
          </CollabLockedField>
          {canEdit && (
            <>
              <Divider style={{ margin: "0 -1.4rem" }} />
              <SwitchSingle
                id="reportFeatured"
                option={{ display: "Feature on the dashboard", value: true }}
                value={featured || undefined}
                saveValueAsString
                layoutStyle="style2"
                disabled={settingFeatured}
                onChange={(k, v) => {
                  const next = v === true;
                  setFeaturedLocal(next);
                  setFeatured({ reportId: reportData?._id, featured: next });
                }}
                infoTooltip={{ text: "Featured reports lead the Reports strip on the dashboard." }}
              />
            </>
          )}
          <Divider style={{ margin: "0 -1.4rem" }}/>
        
          <AlertButton   
            onClick={() => {
              setShowDeleteModal(true);
              setShowOptionsDropdown(false);
            }}
          > 
            Delete Report
          </AlertButton>
        </div>
    </PopoverMenu>
  );
};

// In collab (always-live) mode there is no manual Publish — edits flush
// continuously — so we surface the live save state where Publish used to sit.
const CollabSaveStatus = ({ status }) => {
  const msg =
    status === "saving" ? "Saving…"
    : status === "error" ? "Couldn’t save — retrying…"
    : status === "stalled" ? "Not saved yet — reconnecting…"
    : "All changes saved";
  return (
    <UI_BODY_SEMIBOLD_SM
      color={status === "error" || status === "stalled" ? colors2.red : colors2.greyColor80}
      style={{ alignSelf: "center", whiteSpace: "nowrap" }}
    >
      {msg}
    </UI_BODY_SEMIBOLD_SM>
  );
};

export const ReportsHeader = ({
  navigateBackToReportsListing,
  hideBackButton = false,
}) => {
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);

  const { localReportMeta, setLocalReportMeta } = useReportMeta();
  const { setShowDeleteModal } = useReportModals();
  const { reportData } = useReportData();
  const { readOnly } = useReportMode();
  const { collabActive, reportCollab } = useReportCollab();
  // Public share ("share for all to view") — available to anyone who can view the report.
  // Hidden on the public report page itself. See SHARED_REPORTS_DESIGN.md.
  const { isPublicReport } = useReportSnapshot();
  const { initiateShare, isPreparing, isSharing, shareUrl, closeShareModal } = useShareReport();

  // Only the edited key: in collab this is a single Y.Doc field write, so a
  // stale subtitle snapshot never rides along and clobbers a peer's edit.
  const handleTitleChange = useCallback((k, newTitle) => {
    setLocalReportMeta?.({ title: newTitle });
  }, [setLocalReportMeta]);

  // Always-live editing: everything is continuously saved, so there are never
  // unsaved changes to warn about on the way out.
  const handleBack = () => navigateBackToReportsListing();

  return (
    <StyledReportsHeaderContainer id={"reportsHeader"} readOnly={readOnly}>
      <StyledReportsHeaderTitleSection>
        {!hideBackButton && (
          <ButtonGhost
            icon="CaretLeft"
            iconColor={colors2.grey80}
            onClick={handleBack}
            style={{ padding: 4 }}
          />
        )}
        <BreadcrumbHeader
          typo="UI_TITLE_SM"
          fontWeight={600}
          mutedColor={colors2.grey50}
          separatorColor={colors2.grey50}
          style={{ alignItems: "center", gap: "0.4rem" }}
          items={[
            // breadcrumb root — click "Reports" to go back to all reports
            !hideBackButton && { display: "Reports", onClick: handleBack },
            {
              node: (
                // a crumb, not a full-width field block: keep it shrinkable so
                // the title still truncates inside BreadcrumbHeader's width cap
                // title's Y.Doc key doubles as its lock valuePath (same
                // convention as PPV3's main.title)
                <CollabLockedField
                  session={collabActive ? reportCollab : null}
                  valuePath="title"
                  // one-line crumb: the absolute badge would land on the title text
                  compactChip
                  style={{ display: "flex", minWidth: 0 }}
                >
                  <StyledEditableTitleHover readOnly={readOnly}>
                    <InlineEditableInput
                      value={localReportMeta.title}
                      onChange={handleTitleChange}
                      readOnly={readOnly}
                      id={'reportsHeaderTitle'}
                      placeholder="Enter a title"
                    />
                  </StyledEditableTitleHover>
                </CollabLockedField>
              ),
            },
          ]}
        />
      </StyledReportsHeaderTitleSection>

      <StyledReportsHeaderActions>

        <StyledReportsHeaderButtons>
        {!readOnly && 
          <ReportOptionsMenu
            showOptionsDropdown={showOptionsDropdown}
            setShowOptionsDropdown={setShowOptionsDropdown}
            localReportMeta={localReportMeta}
            setLocalReportMeta={setLocalReportMeta}
            setShowDeleteModal={setShowDeleteModal}
            reportData={reportData}
            // Featuring follows editing, not authorship: in collab a co-editor
            // can restructure the report, so they can feature it too.
            canEdit={!readOnly}
          />
        }
          {!isPublicReport && (
            <ButtonSecondary
              disabled={isPreparing || isSharing}
              icon="Share"
              S
              onClick={initiateShare}
            >
              {isPreparing || isSharing ? "Preparing link…" : "Share"}
            </ButtonSecondary>
          )}
          {/* Always-live: no manual Publish — the live save state is the whole
              story. collabActive-gated so the connecting window (session not
              yet synced) shows nothing rather than a meaningless "saved". */}
          {!readOnly && collabActive && (
            <CollabSaveStatus status={reportCollab.saveStatus} />
          )}
        </StyledReportsHeaderButtons>

      </StyledReportsHeaderActions>

      {shareUrl && (
        <ShareReportModal url={shareUrl} onClose={closeShareModal} />
      )}

    </StyledReportsHeaderContainer>
  );
};
